import React, { useMemo, useState, useEffect } from 'react';
import { Modal } from './Modal';
import { ModalHeader } from './ModalHeader';
import { ModalDownloadButton } from './ModalDownloadButton';
import { ModalFilters } from './ModalFilters';
import { RubricViewToggle } from './RubricViewToggle';
import { EmptyState, ToolEmptyState } from './EmptyState';
import { NO_GRAPH_FOR_MARKS } from './emptyStateCopy';
import DropdownMenu from '../DropdownMenu';
import SmallLoader from '../Loader/SmallLoader';
import { ExportFormatDropdown } from './ExportFormatDropdownNew';
import { getTierClass, getRankClass, getTaskHeaderLabel, STUDENT_SORT_OPTIONS, rankAndSortStudents } from './utils';
import { SECTIONS } from './DownloadManager';
import { useClassInsights } from './ClassInsightsContext';
import {
  catalogClasses,
  selectedClasses,
  selectedSubjects,
  selectedTaskTypes,
  deriveSubjectOptions,
  deriveTaskOptions,
  deriveStudentOptions,
} from './catalogSelectors';
import QuestionTooltip from '../../components2/QuestionTooltip';
import { Tooltip } from '@mui/material';
import { buildRubricBarTooltip, describeRubricShare, rubricLegendBands, rubricShareCounts } from './rubricTooltip';

const TASK_COL_WIDTH = 80;
const SECTION_MIN_WIDTH = 280;
const getSectionWidth = (criteria) =>
  Math.max(SECTION_MIN_WIDTH, (criteria?.tasks?.length || 0) * TASK_COL_WIDTH);

export function RubricsModal({
  open,
  onClose,
  onExited,
  list,
  selectedIds,
  onChange,
  stats,
  percentageStats,
  isLoading,
  isLoadingPercentage,
  sortBy,
  onSortChange,
  viewMode,
  onViewModeChange,
}) {
  const { filters, setFilters, catalog } = useClassInsights();
  const [selectedCriterias, setSelectedCriterias] = useState([]);

  const normalizedPercentageStats = useMemo(() => {
    const selectedRubricId = selectedIds[0] ? String(selectedIds[0]) : null;
    const baseStats = Array.isArray(percentageStats)
      ? percentageStats
      : Array.isArray(percentageStats?.stats)
        ? percentageStats.stats
        : [];
    if (!selectedRubricId) return baseStats;
    const matchedStats = baseStats.filter(
      (item) => String(item?.rubricId || '') === selectedRubricId
    );
    return matchedStats.length > 0 ? matchedStats : baseStats;
  }, [percentageStats, selectedIds]);

  const { taskGroups, rubricRows, questionNumbers } = useMemo(() => {
    if (!Array.isArray(stats) || stats.length === 0) {
      return { taskGroups: [], rubricRows: [], questionNumbers: [null] };
    }

    const selectedRubricId = selectedIds[0] ? String(selectedIds[0]) : null;

    const taskMap = new Map();
    const studentMap = new Map();
    const questionNumberSet = new Set();

    stats.forEach((task) => {
      const taskId = task.taskId;
      const taskTitle = task.taskTitle || task.title || `Task ${taskId}`;

      (task.stats || []).forEach((stat) => {
        if (selectedRubricId && String(stat.rubricId || '') !== selectedRubricId) {
          return;
        }

        const questionKey = stat.questionSerialNumber?.value || 'unknown';
        const questionNumber = Number(questionKey) || 0;
        if (questionNumber > 0) questionNumberSet.add(questionNumber);

        const scores = (stat.criteriaWithLevels || []).map((criteria, index) => ({
          value: criteria.selectedLevel?.value || '',
          tier: criteria.selectedLevel?.tier || '',
          criteriaTitle: criteria.criteriaTitle || `Criterion ${index + 1}`,
        }));

        if (!taskMap.has(taskId)) {
          taskMap.set(taskId, { taskId, taskTitle, criterias: [] });
        }
        const taskEntry = taskMap.get(taskId);
        const nextTitles = scores.map((score, index) => score.criteriaTitle || `Criterion ${index + 1}`);
        if (nextTitles.length > taskEntry.criterias.length) {
          taskEntry.criterias = nextTitles;
        }

        const studentName = stat.studentName || 'Unknown';
        if (!studentMap.has(studentName)) {
          studentMap.set(studentName, {
            name: studentName,
            scoresByTaskQuestion: new Map(),
          });
        }
        studentMap.get(studentName).scoresByTaskQuestion.set(`${taskId}-${questionNumber}`, scores);
      });
    });

    const taskGroups = Array.from(taskMap.values());

    const rubricRows = Array.from(studentMap.values()).map((student) => ({
      name: student.name,
      scoresByTaskQuestion: student.scoresByTaskQuestion,
    }));

    const questionNumbers = Array.from(questionNumberSet).sort((a, b) => a - b);

    return {
      taskGroups,
      rubricRows,
      questionNumbers: questionNumbers.length > 0 ? questionNumbers : [null],
    };
  }, [stats, selectedIds]);

  const sortedRubricRows = useMemo(() => rankAndSortStudents(rubricRows, sortBy), [rubricRows, sortBy]);

  const rubricGraph = useMemo(() => {
    if (!Array.isArray(normalizedPercentageStats) || normalizedPercentageStats.length === 0) {
      return { criteriaGroups: [], availableCriterias: [], legends: [] };
    }

    const criteriaMap = new Map();
    let legends = [];

    normalizedPercentageStats.forEach((rubricItem) => {
      // Extract legends from the first rubric item
      if (rubricItem.legends && Array.isArray(rubricItem.legends) && legends.length === 0) {
        // Get unique legends while preserving order
        const uniqueLegends = [...new Set(rubricItem.legends)];
        legends = uniqueLegends;
      }

      const tasks = rubricItem.tasks || [];

      tasks.forEach((task) => {
        const taskName = task.taskTitle || 'Untitled Task';
        (task.criterias || []).forEach((criteria) => {
          const criteriaName = criteria.title || 'Untitled Criteria';
          const percentages = criteria.studentSharePercentages || {};
          if (!criteriaMap.has(criteriaName)) {
            criteriaMap.set(criteriaName, []);
          }
          criteriaMap.get(criteriaName).push({
            taskName,
            high: Number(percentages.top) || 0,
            middle: Number(percentages.middle) || 0,
            low: Number(percentages.bottom) || 0,
            counts: rubricShareCounts(criteria),
          });
        });
      });
    });

    const criteriaGroups = Array.from(criteriaMap.entries()).map(([criteriaName, tasks]) => ({
      criteriaName,
      tasks,
    }));

    return {
      criteriaGroups,
      availableCriterias: Array.from(criteriaMap.keys()),
      legends,
    };
  }, [normalizedPercentageStats]);


  useEffect(() => {
    if (rubricGraph.availableCriterias.length > 0 && selectedCriterias.length === 0) {
      setSelectedCriterias(rubricGraph.availableCriterias);
    }
  }, [rubricGraph.availableCriterias, selectedCriterias.length]);

  const filteredCriteriaGroups = useMemo(() => (
    rubricGraph.criteriaGroups
      .filter((group) => selectedCriterias.includes(group.criteriaName))
  ), [rubricGraph.criteriaGroups, selectedCriterias]);


  const [selectedLevels, setSelectedLevels] = useState([]);

  const legendLevels = useMemo(() => {
    const legends = rubricGraph.legends || [];
    if (legends.length === 0) {
      return {
        high: ['A'],
        middle: ['B', 'C'],
        low: ['D', 'E'],
      };
    }

    return rubricLegendBands(legends);
  }, [rubricGraph.legends]);

  const getLevelCategory = (level) => {
    if (legendLevels.high.includes(level)) return 'high';
    if (legendLevels.middle.includes(level)) return 'middle';
    if (legendLevels.low.includes(level)) return 'low';
    return 'high';
  };

  const individualGradeData = useMemo(() => {
    if (!Array.isArray(stats) || stats.length === 0) return new Map();

    const selectedRubricId = selectedIds[0] ? String(selectedIds[0]) : null;
    const gradeCountsByTask = new Map();
    const totalStudentsByTask = new Map();
    const studentsByTask = new Map();

    stats.forEach((task) => {
      const taskName = task.taskTitle || task.title || `Task ${task.taskId}`;

      (task.stats || []).forEach((stat) => {
        if (selectedRubricId && String(stat.rubricId || '') !== selectedRubricId) {
          return;
        }

        (stat.criteriaWithLevels || []).forEach((criteria) => {
          const grade = criteria.selectedLevel?.value || '';
          if (!grade) return;

          const criteriaTitle = criteria.criteriaTitle || '';
          const key = `${taskName}-${criteriaTitle}`;

          if (!gradeCountsByTask.has(key)) {
            gradeCountsByTask.set(key, new Map());
            totalStudentsByTask.set(key, 0);
            studentsByTask.set(key, new Set());
          }
          studentsByTask.get(key).add(stat.studentName || '');

          const gradeCounts = gradeCountsByTask.get(key);
          gradeCounts.set(grade, (gradeCounts.get(grade) || 0) + 1);
          totalStudentsByTask.set(key, totalStudentsByTask.get(key) + 1);
        });
      });
    });
    const result = new Map();
    gradeCountsByTask.forEach((gradeCounts, key) => {
      const total = totalStudentsByTask.get(key) || 1;
      const percentages = new Map();
      gradeCounts.forEach((count, grade) => {
        percentages.set(grade, (count / total) * 100);
      });
      result.set(key, {
        percentages,
        counts: gradeCounts,
        marks: totalStudentsByTask.get(key) || 0,
        submissions: studentsByTask.get(key).size,
      });
    });

    return result;
  }, [stats, selectedIds]);

  const visibleCategories = useMemo(() => {
    if (selectedLevels.length === 0) {
      return { high: true, middle: true, low: true };
    }
    const categories = { high: false, middle: false, low: false };
    selectedLevels.forEach(level => {
      const category = getLevelCategory(level);
      categories[category] = true;
    });
    return categories;
  }, [selectedLevels, legendLevels]);

  const maxGraphValue = 100;

  const allLevels = useMemo(() => {
    const legends = rubricGraph.legends || [];
    if (legends.length === 0) {
      return ['A', 'B', 'C', 'D', 'E'];
    }
    return [...new Set(legends)];
  }, [rubricGraph.legends]);

  const shortenLevelText = (text) => {
    if (!text) return '';
    return text.trim().charAt(0);
  };

  const toggleLevelFilter = (level) => {
    setSelectedLevels((prev) => (prev[0] === level ? [] : [level]));
  };

  const toggleCriteriaSelection = (criteriaName) => {
    setSelectedCriterias(prev => {
      if (prev.includes(criteriaName)) {
        return prev.filter(c => c !== criteriaName);
      }
      return [...prev, criteriaName];
    });
  };

  const truncateText = (name, maxLength = 25) => {
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength) + '...';
  };

  const getQuestionSerialLabel = (questionNumber) => {
    const serial = Number(questionNumber);
    if (!Number.isFinite(serial) || serial <= 0) return 'Q-';
    return `Q${serial}`;
  };

  const rubricCsv = sortedRubricRows.flatMap((row) =>
    questionNumbers.map((questionNumber) => {
      const flatScores = {};
      taskGroups.forEach((task) => {
        const scores = row.scoresByTaskQuestion.get(`${task.taskId}-${questionNumber}`) || [];
        const criteriaCount = Math.max(task.criterias.length, scores.length);
        for (let i = 0; i < criteriaCount; i += 1) {
          const score = scores[i];
          const criteriaTitle = task.criterias[i] || score?.criteriaTitle || `Score ${i + 1}`;
          const columnName = `${task.taskTitle} - ${criteriaTitle}`;
          flatScores[columnName] = score?.value || '';
        }
      });
      return {
        Rank: row.rank,
        Student: row.name,
        Question: getQuestionSerialLabel(questionNumber),
        ...flatScores,
      };
    })
  );

  const classes = catalogClasses(catalog);

  const rubricLabels = selectedIds
    .map(id => list.find(r => String(r.id) === String(id)))
    .filter(Boolean)
    .map(r => r.title || r.name || 'Untitled Rubric');

  const classLabels = filters.classIds
    .map(id => classes.find(c => String(c.id) === String(id)))
    .filter(Boolean)
    .map(c => c.title || c.name);

  const selectedClassesData = selectedClasses(catalog, filters);
  const selectedSubjectsData = selectedSubjects(selectedClassesData, filters);
  const selectedTaskTypesData = selectedTaskTypes(selectedSubjectsData, filters);

  const subjectOptions = deriveSubjectOptions(selectedClassesData);
  const taskOptions = deriveTaskOptions(selectedTaskTypesData);
  const studentOptions = deriveStudentOptions(selectedClassesData);

  const subjectLabels = filters.subjectCodes
    ?.map(code => subjectOptions.find(s => s.id === code)?.label)
    .filter(Boolean) || [];

  const taskTypeLabels = filters.taskTypes || [];

  const taskNameLabels = filters.taskIds
    ?.map(id => taskOptions.find(t => String(t.id) === String(id))?.label)
    .filter(Boolean) || [];

  const studentLabels = filters.studentIds
    ?.map(id => studentOptions.find(s => String(s.id) === String(id))?.label)
    .filter(Boolean) || [];

  const dateRange = filters.startDate && filters.endDate
    ? `${filters.startDate} to ${filters.endDate}`
    : null;

  const exportFilters = {
    classLabels,
    rubricLabels,
    dateRange,
    filters: {
      subjects: subjectLabels,
      taskTypes: taskTypeLabels,
      taskIds: taskNameLabels,
      studentIds: studentLabels,
    },
  };

  return (
    <Modal
      open={open}
      onExited={onExited}
      onOverlayClick={onClose}
      className="ci-modal-wide"
      data-section="rubrics-modal"
    >
      <ModalHeader title="Rubric" onClose={onClose}>
        <ExportFormatDropdown
          section={SECTIONS.RUBRICS}
          data={stats}
          csvData={rubricCsv}
          excludedFormatKeys={viewMode === 'graph' ? ['pdf'] : []}
          isModal={true}
          filterData={exportFilters}
          trigger={<ModalDownloadButton />}
        />
      </ModalHeader>

      <ModalFilters
        firstFilterLabel="Rubric"
        firstFilterOptions={list}
        firstFilterValue={selectedIds}
        firstFilterOnChange={(values) => onChange(values.map(String))}
        firstFilterGetOptionLabel={(opt) => opt.title || opt.name || 'Untitled Rubric'}
        firstFilterGetOptionValue={(opt) => String(opt.id)}
        firstFilterSingleSelect
        firstFilterShowCheckboxes
        filters={filters}
        setFilters={setFilters}
        firstFilterShowAllOption={false}
        className="ci-rubric-modal-filters"
      />

      <div className="ci-rubric-view-control">
        <div className="ci-rubric-tabs-section">
          <button
            type="button"
            className={`ci-rubric-tab${selectedLevels.length === 0 ? ' active' : ''}`}
            onClick={() => setSelectedLevels([])}
          >
            Overall
          </button>
          {viewMode === 'graph' && allLevels.length > 0 && (
            <div className="ci-rubric-level-filters">
              {allLevels.map((level) => {
                const shortText = shortenLevelText(level);
                const isActive = selectedLevels.includes(level);
                const category = getLevelCategory(level);
                const needsTooltip = shortText !== level;
                const badgeClasses = `ci-rubric-level-badge ci-rubric-level-badge-${category}${isActive ? ' active' : ''}`;

                const badge = (
                  <button
                    key={level}
                    type="button"
                    className={badgeClasses}
                    onClick={() => toggleLevelFilter(level)}
                  >
                    {shortText}
                  </button>
                );

                return needsTooltip ? (
                  <QuestionTooltip key={level} text={level}>
                    {badge}
                  </QuestionTooltip>
                ) : (
                  badge
                );
              })}
            </div>
          )}
        </div>
        <RubricViewToggle
          mode={viewMode}
          onTable={() => onViewModeChange('table')}
          onGraph={() => onViewModeChange('graph')}
        />
      </div>

      {viewMode === 'table' ? (
        <>
          {!isLoading && sortedRubricRows.length > 0 && (
            <div className="ci-modal-table-controls">
              <div className="ci-modal-sort">
                <span>Sort by:</span>
                <DropdownMenu
                  multiselect
                  singleSelect
                  options={STUDENT_SORT_OPTIONS}
                  selectedValues={[sortBy]}
                  onChange={(values) => onSortChange(values[0] || STUDENT_SORT_OPTIONS[0])}
                  placeholder="Sort"
                  getOptionLabel={(opt) => opt}
                  getOptionValue={(opt) => opt}
                  alwaysWhite
                  compactDisplay
                  showCheckboxes={false}
                  showAllOption={false}
                  triggerFontSize="14px"
                  triggerPadding="8px 12px"
                  triggerWidth="auto"
                  triggerMinWidth="280px"
                />
              </div>
            </div>
          )}
          <div className="ci-modal-table-content">
            {isLoading ? (
              <div className="ci-panel-loading">
                <SmallLoader />
              </div>
            ) : sortedRubricRows.length === 0 ? (
              <ToolEmptyState tool="rubric" />
            ) : (
              <table className="ci-modal-table ci-rubric-table">
                <thead>
                  <tr>
                    <th className="ci-sticky-col"><span data-dd-privacy="mask-user-input">Student</span></th>
                    {taskGroups.length > 0 && <th className="ci-group-separator-cell" aria-hidden="true" />}
                    <th
                      className="ci-rubric-question-serial-header"
                      aria-hidden="true"
                    />
                    <th className="ci-rubric-question-gap-cell" aria-hidden="true" />
                    {taskGroups.map((task, taskIndex) => {
                      const { full, display } = getTaskHeaderLabel(task.taskTitle);
                      const singleTask = taskGroups.length === 1;
                      return (
                      <React.Fragment key={`task-${task.taskId}`}>
                        <th
                          colSpan={Math.max(1, task.criterias.length)}
                          className="ci-question-group-header"
                        >
                          <div
                            className={
                              singleTask
                                ? 'ci-task-group-header-title-wrap ci-task-group-header-title-wrap--single'
                                : 'ci-task-group-header-title-wrap'
                            }
                          >
                            <QuestionTooltip text={full}>
                              <span className="ci-task-group-header-title">
                                {singleTask ? full : display}
                              </span>
                            </QuestionTooltip>
                          </div>
                        </th>
                        {taskIndex < taskGroups.length - 1 && (
                          <th className="ci-group-separator-cell" aria-hidden="true" />
                        )}
                      </React.Fragment>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {sortedRubricRows.map((row) =>
                    questionNumbers.map((questionNumber, rowIndex) => (
                      <tr key={`${row.name}-${questionNumber ?? 'q'}`}>
                        {rowIndex === 0 && (
                          <td className="ci-sticky-col" rowSpan={questionNumbers.length}>
                            <div className="ci-table-student">
                              <span className={getRankClass(row.rank)}>{row.rank}</span>
                              {row.name}
                            </div>
                          </td>
                        )}
                        {taskGroups.length > 0 && <td className="ci-group-separator-cell" aria-hidden="true" />}
                        <td className="ci-rubric-question-serial-cell">
                          <span className="ci-rubric-question-serial-label">
                            {getQuestionSerialLabel(questionNumber)}
                          </span>
                        </td>
                        <td className="ci-rubric-question-gap-cell" aria-hidden="true" />
                        {taskGroups.map((task, taskIndex) => {
                          const criteriaCount = Math.max(1, task.criterias.length);
                          const scores = row.scoresByTaskQuestion.get(`${task.taskId}-${questionNumber}`) || [];
                          return (
                            <React.Fragment key={`${row.name}-${questionNumber ?? 'q'}-${task.taskId}`}>
                              {Array.from({ length: criteriaCount }).map((_, scoreIndex) => {
                                const score = scores[scoreIndex];
                                const value = score?.value || '-';
                                const tier = score?.tier || '';
                                const criteriaTitle = task.criterias[scoreIndex] || score?.criteriaTitle || 'Criterion';
                                const classes = [getTierClass(tier), 'ci-rubric-score-cell'];
                                if (scoreIndex === 0) classes.push('ci-rubric-score-cell--group-start');
                                if (scoreIndex === criteriaCount - 1) classes.push('ci-rubric-score-cell--group-end');
                                return (
                                  <td
                                    key={`${task.taskId}-${questionNumber ?? 'q'}-${scoreIndex}`}
                                    className={classes.join(' ')}
                                  >
                                    <QuestionTooltip text={criteriaTitle}>
                                      <span className="ci-rubric-score-value">{value}</span>
                                    </QuestionTooltip>
                                  </td>
                                );
                              })}
                              {taskIndex < taskGroups.length - 1 && (
                                <td className="ci-group-separator-cell" aria-hidden="true" />
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <div className="ci-rubric-graph-wrap">
          {!isLoadingPercentage && filteredCriteriaGroups.length > 0 && (
            <div className="ci-rubric-criteria-filters">
              {rubricGraph.availableCriterias.map((criteriaName) => (
                <label key={criteriaName} className="ci-criteria-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedCriterias.includes(criteriaName)}
                    onChange={() => toggleCriteriaSelection(criteriaName)}
                  />
                  <span title={criteriaName}>
                    {truncateText(criteriaName)}
                  </span>
                </label>
              ))}
            </div>
          )}

          <div className="ci-rubric-graph-scroll">
          <div className="ci-rubric-graph-area">
            {!isLoadingPercentage && filteredCriteriaGroups.length > 0 && (
              <div className="ci-rubric-y-sticky">
                <div className="ci-rubric-y-label">Share of marked work (%)</div>
                <div className="ci-rubric-y-axis">
                  <span>{maxGraphValue}</span>
                  <span>{Math.floor(maxGraphValue * 0.75)}</span>
                  <span>{Math.floor(maxGraphValue * 0.5)}</span>
                  <span>{Math.floor(maxGraphValue * 0.25)}</span>
                  <span>0</span>
                </div>
              </div>
            )}
            <div style={{
              flex: '0 0 auto',
              minWidth: filteredCriteriaGroups.length > 0
                ? `${filteredCriteriaGroups.reduce((sum, group) => sum + getSectionWidth(group), 0)}px`
                : 'auto',
              width: filteredCriteriaGroups.length > 0
                ? `${filteredCriteriaGroups.reduce((sum, group) => sum + getSectionWidth(group), 0)}px`
                : 'auto'
            }}>
              {!isLoadingPercentage && filteredCriteriaGroups.length > 0 && (
                <div className="ci-rubric-task-headers" style={{
                  display: 'flex',
                  minWidth: `${filteredCriteriaGroups.reduce((sum, group) => sum + getSectionWidth(group), 0)}px`
                }}>
                  {filteredCriteriaGroups.map((criteriaGroup) => (
                    <div key={criteriaGroup.criteriaName} className="ci-rubric-task-header-item" style={{ width: getSectionWidth(criteriaGroup), fontSize: '14px', fontWeight: 400, flexShrink: 0 }}>
                      {criteriaGroup.criteriaName}
                    </div>
                  ))}
                </div>
              )}
              <div className="ci-rubric-graph-container" style={{
                minWidth: filteredCriteriaGroups.length > 0
                  ? `${filteredCriteriaGroups.reduce((sum, group) => sum + getSectionWidth(group), 0)}px`
                  : 'auto'
              }}>
                {isLoadingPercentage ? (
                  <div className="ci-panel-loading">
                    <SmallLoader />
                  </div>
                ) : filteredCriteriaGroups.length === 0 ? (
                  sortedRubricRows.length > 0 ? (
                    <EmptyState {...NO_GRAPH_FOR_MARKS} />
                  ) : (
                    <ToolEmptyState tool="rubric" />
                  )
                ) : (
                  filteredCriteriaGroups.map((criteriaGroup, criteriaIndex) => {
                    const colors = ['#F8EBEB', '#E7FCF9', '#E7EDFC'];
                    const bgColor = colors[criteriaIndex % colors.length];

                    return (
                      <div
                        key={criteriaGroup.criteriaName}
                        className="ci-rubric-task-section"
                        style={{
                          backgroundColor: bgColor,
                          width: getSectionWidth(criteriaGroup),
                          flexShrink: 0,
                        }}
                      >
                        <div className="ci-rubric-task-bars-container">
                          {criteriaGroup.tasks.map((task, taskIndex) => {
                            const maxHeight = 200;
                            if (selectedLevels.length > 0) {
                              const key = `${task.taskName}-${criteriaGroup.criteriaName}`;
                              const gradeData = individualGradeData.get(key);

                              return (
                                <div
                                  key={`${criteriaGroup.criteriaName}-${task.taskName}-${taskIndex}`}
                                  className="ci-rubric-criteria-column"
                                >
                                  <div className="ci-rubric-graph-bars">
                                    {selectedLevels.map((level) => {
                                      const percentage = gradeData?.percentages.get(level) || 0;
                                      const height = maxGraphValue > 0 ? (percentage / maxGraphValue) * maxHeight : 0;
                                      const category = getLevelCategory(level);
                                      // JED-1020: the same count hover text as the overall graph.
                                      const text = gradeData
                                        ? describeRubricShare({
                                            label: level,
                                            count: gradeData.counts.get(level) || 0,
                                            marks: gradeData.marks,
                                            submissions: gradeData.submissions,
                                            pct: percentage,
                                          })
                                        : null;
                                      const bar = (
                                        <div
                                          key={level}
                                          className={`ci-rubric-bar ci-rubric-bar-${category}`}
                                          style={{ height: `${height}px` }}
                                          {...(text ? { role: 'img', 'aria-label': text } : {})}
                                        />
                                      );
                                      return text ? (
                                        <Tooltip key={level} title={text} placement="top" arrow>
                                          {bar}
                                        </Tooltip>
                                      ) : (
                                        bar
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            }
                            const highHeight = maxGraphValue > 0 ? (task.high / maxGraphValue) * maxHeight : 0;
                            const middleHeight = maxGraphValue > 0 ? (task.middle / maxGraphValue) * maxHeight : 0;
                            const lowHeight = maxGraphValue > 0 ? (task.low / maxGraphValue) * maxHeight : 0;

                            return (
                              <div
                                key={`${criteriaGroup.criteriaName}-${task.taskName}-${taskIndex}`}
                                className="ci-rubric-criteria-column"
                              >
                                <div className="ci-rubric-graph-bars">
                                  {[
                                    { key: 'high', height: highHeight, pct: task.high },
                                    { key: 'middle', height: middleHeight, pct: task.middle },
                                    { key: 'low', height: lowHeight, pct: task.low },
                                  ]
                                    .filter(({ key }) => visibleCategories[key])
                                    .map(({ key, height, pct }) => {
                                      const text = buildRubricBarTooltip({ band: key, grades: legendLevels[key], pct, counts: task.counts });
                                      return (
                                        <Tooltip key={key} title={text} placement="top" arrow>
                                          <div
                                            className={`ci-rubric-bar ci-rubric-bar-${key}`}
                                            style={{ height: `${height}px` }}
                                            role="img"
                                            aria-label={text}
                                          />
                                        </Tooltip>
                                      );
                                    })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              {filteredCriteriaGroups.length > 0 && !isLoadingPercentage && (
                <div className="ci-rubric-criteria-labels-row" style={{
                  display: 'flex',
                  minWidth: `${filteredCriteriaGroups.reduce((sum, group) => sum + getSectionWidth(group), 0)}px`
                }}>
                  {filteredCriteriaGroups.map((criteriaGroup) => (
                    <div key={`labels-${criteriaGroup.criteriaName}`} className="ci-rubric-task-criteria-labels" style={{ width: getSectionWidth(criteriaGroup), flexShrink: 0 }}>
                      {criteriaGroup.tasks.map((task, taskIndex) => (
                        <div
                          key={`label-${criteriaGroup.criteriaName}-${task.taskName}-${taskIndex}`}
                          className="ci-rubric-criteria-label-item"
                          title={task.taskName}
                        >
                          {truncateText(task.taskName, 20)}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          </div>
          {filteredCriteriaGroups.length > 0 && !isLoadingPercentage && (
            <div className="ci-rubric-x-label">Tasks</div>
          )}
          <div className="ci-rubric-graph-legend">
            {legendLevels.high.length > 0 && (
              <span>
                <span className="ci-rubric-legend-dot ci-rubric-legend-high" />
                {legendLevels.high.map((level, index) => {
                  const fullText = String(level || '');
                  const displayText = shortenLevelText(fullText);
                  return (
                    <React.Fragment key={`high-${level}-${index}`}>
                      {index > 0 && ', '}
                      <QuestionTooltip text={fullText}>
                        <span style={{ cursor: 'help' }}>{displayText}</span>
                      </QuestionTooltip>
                    </React.Fragment>
                  );
                })}
              </span>
            )}
            {legendLevels.middle.length > 0 && (
              <span>
                <span className="ci-rubric-legend-dot ci-rubric-legend-middle" />
                {legendLevels.middle.map((level, index) => {
                  const fullText = String(level || '');
                  const displayText = shortenLevelText(fullText);
                  return (
                    <React.Fragment key={`middle-${level}-${index}`}>
                      {index > 0 && ', '}
                      <QuestionTooltip text={fullText}>
                        <span style={{ cursor: 'help' }}>{displayText}</span>
                      </QuestionTooltip>
                    </React.Fragment>
                  );
                })}
              </span>
            )}
            {legendLevels.low.length > 0 && (
              <span>
                <span className="ci-rubric-legend-dot ci-rubric-legend-low" />
                {legendLevels.low.map((level, index) => {
                  const fullText = String(level || '');
                  const displayText = shortenLevelText(fullText);
                  return (
                    <React.Fragment key={`low-${level}-${index}`}>
                      {index > 0 && ', '}
                      <QuestionTooltip text={fullText}>
                        <span style={{ cursor: 'help' }}>{displayText}</span>
                      </QuestionTooltip>
                    </React.Fragment>
                  );
                })}
              </span>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
